import { useLocalization } from "@fluent/react";
import { TimelineStep } from "@lending/components/StatusTimeline";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { MembershipShareCard } from "./MembershipShareCard";
import styles from "../Status.module.css";
import type { ProductType } from "../../../../../navigation/types";
import type { AccountApplication } from "../types";

interface OpenedTimelineContentProps {
  accountApplication: AccountApplication;
  enrollButtonLabel: string;
  onEnrollNow: () => void;
  productType: ProductType;
}

function OpenedTimelineContent({
  accountApplication,
  enrollButtonLabel,
  onEnrollNow,
  productType,
}: OpenedTimelineContentProps) {
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const membershipProduct = accountApplication.selected_products?.[0];
  const membershipAccountNumber = membershipProduct?.account?.account_number_primary ?? undefined;

  let step1Title: string;
  let step1Description: string;
  let step2Title: string;
  let step2Description: string;
  let step2Time: string;
  let step3Title: string;
  let step3Description: string;
  let step3Time: string;

  if (productType === "credit-card") {
    step1Title = l10n.getString(
      "membership-share-status-step-1-title-credit-card",
      null,
      "Application submitted"
    );
    step1Description = l10n.getString(
      "membership-share-status-step-1-description-credit-card",
      null,
      "We're reviewing your application and will keep you updated. In the meantime, you can set up your membership account."
    );
    step2Title = l10n.getString(
      "membership-share-status-step-2-title-credit-card",
      null,
      "Application review"
    );
    step2Description = l10n.getString(
      "membership-share-status-step-2-description-credit-card",
      null,
      "An officer will reach out to finalize the details."
    );
    step2Time = l10n.getString("membership-share-status-step-2-time-credit-card", null, "2-3 days");
    step3Title = l10n.getString(
      "membership-share-status-step-3-title-credit-card",
      null,
      "Card received"
    );
    step3Description = l10n.getString(
      "membership-share-status-step-3-description-credit-card",
      null,
      "Your digital card will be available immediately upon approval. A physical card will arrive 2-3 days after your application is approved."
    );
    step3Time = l10n.getString("membership-share-status-step-3-time-credit-card", null, "3-5 days");
  } else if (productType === "auto-loan") {
    step1Title = l10n.getString(
      "membership-share-status-step-1-title-auto-loan",
      null,
      "Application received"
    );
    step1Description = l10n.getString(
      "membership-share-status-step-1-description-auto-loan",
      null,
      "We're reviewing your application and will keep you updated."
    );
    step2Title = l10n.getString(
      "membership-share-status-step-2-title-auto-loan",
      null,
      "Loan review"
    );
    step2Description = l10n.getString(
      "membership-share-status-step-2-description-auto-loan",
      null,
      "A loan officer will reach out to finalize your loan terms."
    );
    step2Time = l10n.getString("membership-share-status-step-2-time-auto-loan", null, "2-3 days");
    step3Title = l10n.getString(
      "membership-share-status-step-3-title-auto-loan",
      null,
      "Funds received"
    );
    step3Description = l10n.getString(
      "membership-share-status-step-3-description-auto-loan",
      null,
      "Funds will be deposited to your account within 24 hours of approval."
    );
    step3Time = l10n.getString("membership-share-status-step-3-time-auto-loan", null, "3-5 days");
  } else {
    step1Title = l10n.getString(
      "membership-share-status-step-1-title-personal-loan",
      null,
      "Application received"
    );
    step1Description = l10n.getString(
      "membership-share-status-step-1-description-personal-loan",
      null,
      "We're reviewing your application and will keep you updated. In the meantime, you can set up your membership account."
    );
    step2Title = l10n.getString(
      "membership-share-status-step-2-title-personal-loan",
      null,
      "Loan review"
    );
    step2Description = l10n.getString(
      "membership-share-status-step-2-description-personal-loan",
      null,
      "A loan officer will reach out to finalize your loan terms."
    );
    step2Time = l10n.getString(
      "membership-share-status-step-2-time-personal-loan",
      null,
      "2-3 days"
    );
    step3Title = l10n.getString(
      "membership-share-status-step-3-title-personal-loan",
      null,
      "Funds received"
    );
    step3Description = l10n.getString(
      "membership-share-status-step-3-description-personal-loan",
      null,
      "Funds will be deposited to your account within 24 hours of approval."
    );
    step3Time = l10n.getString(
      "membership-share-status-step-3-time-personal-loan",
      null,
      "3-5 days"
    );
  }

  return (
    <div className={styles.timeline}>
      <TimelineStep
        title={step1Title}
        description={step1Description}
        isComplete
        isPrimary
        showLine
        stepNumber={1}
      >
        {membershipProduct?.account && membershipAccountNumber && (
          <MembershipShareCard
            productName={membershipProduct.name ?? ""}
            accountNumber={membershipAccountNumber}
            routingNumber={institution.routing_number}
            enrollButtonLabel={enrollButtonLabel}
            onEnrollNow={onEnrollNow}
          />
        )}
      </TimelineStep>

      <TimelineStep
        title={step2Title}
        description={step2Description}
        estimatedTime={step2Time}
        isComplete={false}
        showLine
        stepNumber={2}
      />

      <TimelineStep
        title={step3Title}
        description={step3Description}
        estimatedTime={step3Time}
        isComplete={false}
        stepNumber={3}
      />
    </div>
  );
}

interface ReviewTimelineContentProps {
  productType: ProductType;
}

function ReviewTimelineContent({ productType }: ReviewTimelineContentProps) {
  const { l10n } = useLocalization();

  let step1Title: string;
  let step1Description: string;
  let step2Title: string;
  let step2Description: string;
  let step2Time: string;
  let step3Title: string;
  let step3Description: string;
  let step3Time: string;

  if (productType === "credit-card") {
    step1Title = l10n.getString(
      "membership-share-status-step-1-title-credit-card",
      null,
      "Application submitted"
    );
    step1Description = l10n.getString(
      "membership-share-status-step-1-description-review-credit-card",
      null,
      "We're reviewing your application and will keep you updated every step of the way."
    );
    step2Title = l10n.getString(
      "membership-share-status-step-2-title-credit-card",
      null,
      "Application review"
    );
    step2Description = l10n.getString(
      "membership-share-status-step-2-description-credit-card",
      null,
      "An officer will reach out to finalize the details."
    );
    step2Time = l10n.getString("membership-share-status-step-2-time-credit-card", null, "2-3 days");
    step3Title = l10n.getString(
      "membership-share-status-step-3-title-credit-card",
      null,
      "Card received"
    );
    step3Description = l10n.getString(
      "membership-share-status-step-3-description-credit-card",
      null,
      "Your digital card will be available immediately upon approval. A physical card will arrive 2-3 days after your application is approved."
    );
    step3Time = l10n.getString("membership-share-status-step-3-time-credit-card", null, "3-5 days");
  } else if (productType === "auto-loan") {
    step1Title = l10n.getString(
      "membership-share-status-step-1-title-auto-loan",
      null,
      "Application received"
    );
    step1Description = l10n.getString(
      "membership-share-status-step-1-description-review-auto-loan",
      null,
      "We're reviewing your application and will keep you updated."
    );
    step2Title = l10n.getString(
      "membership-share-status-step-2-title-auto-loan",
      null,
      "Loan review"
    );
    step2Description = l10n.getString(
      "membership-share-status-step-2-description-auto-loan",
      null,
      "A loan officer will reach out to finalize your loan terms."
    );
    step2Time = l10n.getString("membership-share-status-step-2-time-auto-loan", null, "2-3 days");
    step3Title = l10n.getString(
      "membership-share-status-step-3-title-auto-loan",
      null,
      "Funds received"
    );
    step3Description = l10n.getString(
      "membership-share-status-step-3-description-auto-loan",
      null,
      "Funds will be deposited to your account within 24 hours of approval."
    );
    step3Time = l10n.getString("membership-share-status-step-3-time-auto-loan", null, "3-5 days");
  } else {
    step1Title = l10n.getString(
      "membership-share-status-step-1-title-personal-loan",
      null,
      "Application received"
    );
    step1Description = l10n.getString(
      "membership-share-status-step-1-description-review-personal-loan",
      null,
      "We're reviewing your application and will keep you updated every step of the way."
    );
    step2Title = l10n.getString(
      "membership-share-status-step-2-title-personal-loan",
      null,
      "Loan review"
    );
    step2Description = l10n.getString(
      "membership-share-status-step-2-description-personal-loan",
      null,
      "A loan officer will reach out to finalize your loan terms."
    );
    step2Time = l10n.getString(
      "membership-share-status-step-2-time-personal-loan",
      null,
      "2-3 days"
    );
    step3Title = l10n.getString(
      "membership-share-status-step-3-title-personal-loan",
      null,
      "Funds received"
    );
    step3Description = l10n.getString(
      "membership-share-status-step-3-description-personal-loan",
      null,
      "Funds will be deposited to your account within 24 hours of approval."
    );
    step3Time = l10n.getString(
      "membership-share-status-step-3-time-personal-loan",
      null,
      "3-5 days"
    );
  }

  return (
    <div className={styles.timeline}>
      <TimelineStep
        title={step1Title}
        description={step1Description}
        isComplete
        isPrimary
        showLine
        stepNumber={1}
      />

      <TimelineStep
        title={step2Title}
        description={step2Description}
        estimatedTime={step2Time}
        isComplete={false}
        showLine
        stepNumber={2}
      />

      <TimelineStep
        title={step3Title}
        description={step3Description}
        estimatedTime={step3Time}
        isComplete={false}
        stepNumber={3}
      />
    </div>
  );
}

export { OpenedTimelineContent, ReviewTimelineContent };
